import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { getSessionId } from '../utils/learningSession';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Get token from authStore
const getToken = () => {
  return useAuthStore.getState().token;
};

// ==================== Types ====================

export type ActivityType = 'reading' | 'video' | 'selfExplanation' | 'survey' | 'review' | 'exercise' | 'quiz';

export interface ReadingData {
  scrollDepth?: number;
  lastScrollPosition?: number;
}

export interface VideoData {
  videoUrl?: string;
  watchTime?: number;
  videoDuration?: number;
  watchPercentage?: number;
  lastTimestamp?: number;
  playbackSpeed?: number;
}

export interface QuizQuestionResponse {
  questionId: string;
  questionText?: string;
  selectedAnswer?: number;
  correctAnswer?: number;
  isCorrect?: boolean;
  timeSpent?: number;
  answeredAt?: Date;
}

export interface QuizData {
  totalQuestions?: number;
  correctAnswers?: number;
  score?: number;
  passed?: boolean;
  attempts?: number;
  responses?: QuizQuestionResponse[];
}

export interface SelfExplanationData {
  explanation?: string;
  understandingLevel?: 'low' | 'medium' | 'high';
  wordCount?: number;
  edited?: boolean;
  editCount?: number;
}

export interface SurveyQuestionResponse {
  questionId: string;
  questionText?: string;
  answer?: any;
}

export interface SurveyData {
  surveyId?: string;
  responses?: SurveyQuestionResponse[];
  completed?: boolean;
}

export interface ReviewData {
  notes?: string;
  reviewedItems?: string[];
}

export interface ExerciseResponse {
  exerciseId: string;
  exerciseOrder?: number;
  questionType: 'choice' | 'text' | 'drawing' | 'parameterized_choice' | 'parameterized_text';
  questionText?: string;
  selectedParameterId?: string;
  selectedParameter?: any;
  finalAnswer?: any;
  correctAnswer?: any;
  isCorrect?: boolean;
  isCompleted?: boolean;
  totalAttempts?: number;
  totalTimeSpent?: number;
}

export interface ExerciseData {
  topicId?: string;
  totalExercises?: number;
  completedExercises?: number;
  responses?: ExerciseResponse[];
  score?: number;
  passed?: boolean;
}

export interface MasteryStatus {
  masteryStatus: 'in_progress' | 'mastered' | 'needs_review';
  consecutiveCorrect: number;
  consecutiveIncorrect: number;
  totalAttempts: number;
  masteredAt?: Date | null;
}

export interface ExerciseAttempt {
  exerciseId: string;
  exerciseOrder?: number;
  questionType: 'choice' | 'text' | 'drawing' | 'parameterized_choice' | 'parameterized_text';
  questionText?: string;
  selectedParameterId?: string;
  selectedParameter?: any;
  finalAnswer: any;
  correctAnswer: any;
  isCorrect: boolean;
  totalTimeSpent?: number;
}

export interface ActivityResponse {
  _id: string;
  studentId: string;
  courseId: string;
  chapterId?: string;
  sectionId?: string;
  topicId: string;
  activityId: string;
  activityType: ActivityType;
  startedAt: Date;
  completedAt?: Date;
  timeSpent: number;
  isCompleted: boolean;
  readingData?: ReadingData;
  videoData?: VideoData;
  quizData?: QuizData;
  selfExplanationData?: SelfExplanationData;
  surveyData?: SurveyData;
  reviewData?: ReviewData;
  exerciseData?: ExerciseData;
  courseProgress?: {
    progressPercentage: number;
    totalActivitiesCompleted: number;
    totalActivitiesRequired: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface TopicProgress {
  topicId: any;
  sectionId?: string;
  chapterId?: string;
  completedActivities: string[];
  totalActivities: number;
  completedAt?: Date;
}

export interface CourseProgress {
  _id: string;
  studentId: string;
  courseId: string;
  startedAt: Date;
  lastAccessedAt: Date;
  completedAt?: Date;
  totalActivitiesCompleted: number;
  totalActivitiesRequired: number;
  progressPercentage: number;
  totalTimeSpent: number;
  topicProgress: TopicProgress[];
  createdAt: Date;
  updatedAt: Date;
}

export interface SaveActivityResponseParams {
  courseId: string;
  chapterId?: string;
  sectionId?: string;
  topicId: string;
  activityId: string;
  activityType: ActivityType;
  timeSpent?: number;
  isCompleted?: boolean;
  /** Learning session identifier; supplied automatically when omitted. */
  sessionId?: string;
  readingData?: ReadingData;
  videoData?: VideoData;
  quizData?: QuizData;
  selfExplanationData?: SelfExplanationData;
  surveyData?: SurveyData;
  reviewData?: ReviewData;
  exerciseData?: ExerciseData;
}

// ==================== Progress API ====================

/**
 * Get student's progress for a specific course
 */
export const getCourseProgress = async (courseId: string): Promise<CourseProgress> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/student-activities/progress/${courseId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

/**
 * Initialize course progress with total activities count
 */
export const initCourseProgress = async (courseId: string): Promise<CourseProgress> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/student-activities/progress/${courseId}/init`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// ==================== Activity Response API ====================

/**
 * Save an activity response
 */
export const saveActivityResponse = async (
  params: SaveActivityResponseParams
): Promise<ActivityResponse> => {
  const token = getToken();
  // New clients use the separate foreground ledger. These completion payloads
  // must not also feed wall-clock/cumulative legacy summary totals.
  const {timeSpent: _legacyTime, ...withoutTime} = params;
  const response = await axios.post(
    `${API_BASE_URL}/student-activities/responses`,
    // Attach the learning session id centrally so every activity type carries
    // it. Without this, session boundaries are unrecoverable.
    { ...withoutTime, sessionId: params.sessionId || getSessionId() },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

/**
 * Get all activity responses for a student in a course
 */
export const getActivityResponses = async (
  courseId: string,
  filters?: {
    activityType?: ActivityType;
    topicId?: string;
  }
): Promise<ActivityResponse[]> => {
  const token = getToken();
  const params = new URLSearchParams();
  if (filters?.activityType) params.append('activityType', filters.activityType);
  if (filters?.topicId) params.append('topicId', filters.topicId);

  const response = await axios.get(
    `${API_BASE_URL}/student-activities/responses/${courseId}?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

/**
 * Get a specific activity response
 */
export const getActivityResponse = async (
  courseId: string,
  topicId: string,
  activityId: string
): Promise<ActivityResponse | null> => {
  const token = getToken();
  try {
    const response = await axios.get(
      `${API_BASE_URL}/student-activities/responses/${courseId}/${topicId}/${activityId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Get all completed activity IDs for a specific topic
 */
export const getTopicCompletedActivities = async (
  courseId: string,
  topicId: string
): Promise<string[]> => {
  const token = getToken();
  try {
    const response = await axios.get(
      `${API_BASE_URL}/student-activities/responses/${courseId}?topicId=${topicId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    // Filter completed activities and return their IDs
    const completedActivities = response.data.data
      .filter((r: ActivityResponse) => r.isCompleted)
      .map((r: ActivityResponse) => r.activityId);
    return completedActivities;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

// ==================== Convenience Methods ====================

/**
 * Save reading activity progress
 */
export const saveReadingProgress = async (
  courseId: string,
  topicId: string,
  activityId: string,
  data: {
    chapterId?: string;
    sectionId?: string;
    timeSpent?: number;
    scrollDepth?: number;
    lastScrollPosition?: number;
    isCompleted?: boolean;
  }
): Promise<ActivityResponse> => {
  return saveActivityResponse({
    courseId,
    topicId,
    activityId,
    activityType: 'reading',
    chapterId: data.chapterId,
    sectionId: data.sectionId,
    timeSpent: data.timeSpent,
    isCompleted: data.isCompleted,
    readingData: {
      scrollDepth: data.scrollDepth,
      lastScrollPosition: data.lastScrollPosition
    }
  });
};

/**
 * Save video activity progress
 */
export const saveVideoProgress = async (
  courseId: string,
  topicId: string,
  activityId: string,
  data: {
    chapterId?: string;
    sectionId?: string;
    videoUrl?: string;
    watchTime?: number;
    videoDuration?: number;
    lastTimestamp?: number;
    playbackSpeed?: number;
    isCompleted?: boolean;
  }
): Promise<ActivityResponse> => {
  return saveActivityResponse({
    courseId,
    topicId,
    activityId,
    activityType: 'video',
    chapterId: data.chapterId,
    sectionId: data.sectionId,
    timeSpent: data.watchTime,
    isCompleted: data.isCompleted,
    videoData: {
      videoUrl: data.videoUrl,
      videoDuration: data.videoDuration,
      // The page's timer is not verified media playback. Do not persist it as watch time.
      lastTimestamp: data.lastTimestamp,
      playbackSpeed: data.playbackSpeed
    }
  });
};

/**
 * Save quiz activity result
 */
export const saveQuizResult = async (
  courseId: string,
  topicId: string,
  activityId: string,
  data: {
    chapterId?: string;
    sectionId?: string;
    timeSpent?: number;
    responses: QuizQuestionResponse[];
    isCompleted?: boolean;
  }
): Promise<ActivityResponse> => {
  const correctAnswers = data.responses.filter(r => r.isCorrect).length;
  const totalQuestions = data.responses.length;
  const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  return saveActivityResponse({
    courseId,
    topicId,
    activityId,
    activityType: 'quiz',
    chapterId: data.chapterId,
    sectionId: data.sectionId,
    timeSpent: data.timeSpent,
    isCompleted: data.isCompleted ?? true,
    quizData: {
      totalQuestions,
      correctAnswers,
      score,
      passed: score >= 60,
      attempts: 1,
      responses: data.responses
    }
  });
};

/**
 * Save self-explanation activity
 */
export const saveSelfExplanation = async (
  courseId: string,
  topicId: string,
  activityId: string,
  data: {
    chapterId?: string;
    sectionId?: string;
    explanation: string;
    understandingLevel: 'low' | 'medium' | 'high';
    timeSpent?: number;
    isCompleted?: boolean;
  }
): Promise<ActivityResponse> => {
  return saveActivityResponse({
    courseId,
    topicId,
    activityId,
    activityType: 'selfExplanation',
    chapterId: data.chapterId,
    sectionId: data.sectionId,
    timeSpent: data.timeSpent,
    isCompleted: data.isCompleted ?? true,
    selfExplanationData: {
      explanation: data.explanation,
      understandingLevel: data.understandingLevel,
      wordCount: data.explanation.split(/\s+/).length
    }
  });
};

/**
 * Save survey activity
 */
export const saveSurveyResponse = async (
  courseId: string,
  topicId: string,
  activityId: string,
  data: {
    chapterId?: string;
    sectionId?: string;
    surveyId: string;
    responses: SurveyQuestionResponse[];
    timeSpent?: number;
    isCompleted?: boolean;
  }
): Promise<ActivityResponse> => {
  return saveActivityResponse({
    courseId,
    topicId,
    activityId,
    activityType: 'survey',
    chapterId: data.chapterId,
    sectionId: data.sectionId,
    timeSpent: data.timeSpent,
    isCompleted: data.isCompleted ?? true,
    surveyData: {
      surveyId: data.surveyId,
      responses: data.responses,
      completed: data.isCompleted ?? true
    }
  });
};

/**
 * Save exercise activity with parameters and student answers
 */
export const saveExerciseResponse = async (
  courseId: string,
  topicId: string,
  activityId: string,
  data: {
    activityType?: 'quiz' | 'exercise';
    chapterId?: string;
    sectionId?: string;
    responses: ExerciseResponse[];
    timeSpent?: number;
    isCompleted?: boolean;
  }
): Promise<ActivityResponse> => {
  // Calculate score from responses
  const completedResponses = data.responses.filter(r => r.isCompleted);
  const correctResponses = completedResponses.filter(r => r.isCorrect);
  const score = completedResponses.length > 0
    ? Math.round((correctResponses.length / completedResponses.length) * 100)
    : 0;

  return saveActivityResponse({
    courseId,
    topicId,
    activityId,
    activityType: data.activityType || 'exercise',
    chapterId: data.chapterId,
    sectionId: data.sectionId,
    timeSpent: data.timeSpent,
    isCompleted: data.isCompleted ?? true,
    exerciseData: {
      topicId,
      totalExercises: data.responses.length,
      completedExercises: completedResponses.length,
      responses: data.responses,
      score,
      passed: score >= 60
    }
  });
};

// ==================== Exercise Mastery API ====================

/**
 * Save a single exercise attempt and update mastery status
 */
export const saveExerciseAttempt = async (
  courseId: string,
  topicId: string,
  activityId: string,
  attempt: ExerciseAttempt,
  additionalData?: {
    activityType?: 'quiz' | 'exercise';
    chapterId?: string;
    sectionId?: string;
  }
): Promise<{ success: boolean; data: { response: ActivityResponse; mastery: MasteryStatus } }> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.post(
    `${API_BASE_URL}/student-activities/exercise-attempt`,
    {
      courseId,
      topicId,
      activityId,
      attempt,
      activityType: additionalData?.activityType || 'exercise',
      chapterId: additionalData?.chapterId,
      sectionId: additionalData?.sectionId
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * Get mastery status for a specific exercise activity
 */
export const getExerciseMastery = async (
  courseId: string,
  topicId: string,
  activityId: string
): Promise<MasteryStatus> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(
    `${API_BASE_URL}/student-activities/mastery/${courseId}/${topicId}/${activityId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );

  return response.data.data;
};

/**
 * Mark a topic as completed
 */
export const markTopicCompleted = async (
  courseId: string,
  topicId: string,
  chapterId?: string,
  sectionId?: string
): Promise<CourseProgress> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.post(
    `${API_BASE_URL}/student-activities/topics/${courseId}/${topicId}/complete`,
    { chapterId, sectionId },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// ==================== Course Summary API ====================

/**
 * Course Summary interface
 */
export interface CourseSummary {
  studentId: string;
  courseId: string;
  totalStudyTime: number;
  totalSessions: number;
  lastAccessAt: Date;
  startedAt: Date;
  overallProgress: number;
  chapters: Map<string, {
    isUnlocked: boolean;
    unlockedAt?: Date;
    studyTime: number;
    sessions: number;
    totalTopics: number;
    completedTopics: number;
    progress: number;
    sections?: Map<string, {
      studyTime: number;
      sessions: number;
      totalTopics: number;
      completedTopics: number;
      progress: number;
    }>;
  }>;
  topics: Map<string, {
    chapterId: string;
    sectionId?: string;
    isCompleted: boolean;
    completedAt?: Date;
    completionCount: number;
    studyTime: number;
    sessions: number;
    totalActivities: number;
    completedActivities: number;
    activityProgress: number;
    exerciseAttempts: number;
    exerciseCorrectCount: number;
    exerciseCorrectRate: number;
    masteryLevel: 'not_started' | 'learning' | 'reviewing' | 'mastered';
    consecutiveCorrect: number;
  }>;
  behavior: {
    studyStreak: number;
    longestStreak: number;
    averageSessionDuration: number;
  };
  goals: {
    dailyGoal: number;
    dailyProgress: number;
    weeklyGoal: number;
    weeklyProgress: number;
  };
}

/**
 * Get student's course learning summary
 */
export const getCourseSummary = async (
  courseId: string
): Promise<CourseSummary | null> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(
    `${API_BASE_URL}/student-activities/summary/${courseId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data.data;
};

/**
 * Get unlocked chapter IDs for a course
 */
export const getUnlockedChapters = async (
  courseId: string
): Promise<string[]> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(
    `${API_BASE_URL}/student-activities/summary/${courseId}/unlocked-chapters`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data.data || [];
};

/**
 * Get completed topic IDs for a course
 */
export const getCompletedTopicsFromSummary = async (
  courseId: string
): Promise<string[]> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(
    `${API_BASE_URL}/student-activities/summary/${courseId}/completed-topics`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data.data || [];
};

/**
 * Initialize course summary from existing activities (migration)
 */
export const initCourseSummary = async (
  courseId: string
): Promise<CourseSummary | null> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.post(
    `${API_BASE_URL}/student-activities/summary/${courseId}/init`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data.data;
};

/**
 * Update student's current learning position (topic/chapter)
 * Called when user navigates to a topic without completing an activity
 */
export const updateCurrentPosition = async (
  courseId: string,
  topicId: string,
  chapterId?: string
): Promise<void> => {
  const token = getToken();
  if (!token) return;

  await axios.put(
    `${API_BASE_URL}/student-activities/current-position/${courseId}`,
    { topicId, chapterId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

// ==================== Optimized Course Load ====================

/**
 * Interface for optimized course load data
 */
export interface CourseLoadData {
  completedTopicIds: string[];
  unlockedChapterIds: string[];
  currentTopicId: string | null;
  currentChapterId: string | null;
  currentTopicCompletedActivities: string[];
  overallProgress: number;
  lastAccessAt: Date | null;
}

/**
 * Get all data needed for course page load in a single request
 * This is an optimized endpoint that replaces multiple separate API calls
 */
export const getCourseLoadData = async (
  courseId: string
): Promise<CourseLoadData> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(
    `${API_BASE_URL}/student-activities/course-load/${courseId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data.data;
};
